import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  createAssetApi,
  CreateAssetPayload,
  fetchAssetTypesApi,
  AssetType,
} from 'src/services/assetsServices';
import { fetchAllDataApi as fetchFunctionsApi } from 'src/services/functionServices';

interface SimpleOption {
  id: string;
  name: string;
}

const CreateAsset: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [functionsList, setFunctionsList] = useState<SimpleOption[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<CreateAssetPayload>({
    assetName: '',
    assetTypeId: '',
    ownerId: '',
    custodianId: '',
    date: '',
    posture: null,
    compliance: null,
    fields: {},
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [functionsRes, typesRes] = await Promise.all([
          fetchFunctionsApi(),
          fetchAssetTypesApi(),
        ]);
        setFunctionsList(functionsRes.data || []);
        setAssetTypes(typesRes || []);
      } catch {
        setFunctionsList([]);
        setAssetTypes([]);
      }
    };

    loadData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'posture'
          ? (value as CreateAssetPayload['posture'])
          : name === 'compliance'
          ? (value || null)
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createAssetApi(formData);
      toast.success(
        t('assets.create.toast.success', { defaultValue: 'Asset created successfully' }),
      );
      navigate('/assets');
    } catch (error: any) {
      toast.error(
        error?.message ||
          t('assets.create.toast.failed', { defaultValue: 'Failed to create asset' }),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t('header.you_are_here')}</BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem onClick={() => navigate('/assets')} style={{ cursor: 'pointer' }}>
          {t('assets.titlePage') || 'Assets Management'}
        </BreadcrumbItem>
        <span className="mx-2">/</span>
        <BreadcrumbItem active>
          {t('assets.create.title', { defaultValue: 'Create Asset' })}
        </BreadcrumbItem>
      </Breadcrumb>

      <h4 className="mb-lg px-2">
        {t('assets.create.title', { defaultValue: 'Create Asset' })}
      </h4>

      <div
        className="container_info"
        style={{ padding: '40px 20px', backgroundColor: 'var(--widget-bg)', zoom: 0.9 }}
      >
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">
              {t('assets.create.fields.assetName', { defaultValue: 'Asset Name' })}
            </label>
            <input
              type="text"
              name="assetName"
              className="form-control"
              value={formData.assetName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">
              {t('assets.create.fields.type', { defaultValue: 'Type' })}
            </label>
            <select
              name="assetTypeId"
              className="form-select"
              value={formData.assetTypeId}
              onChange={handleChange}
              required
            >
              <option value="">
                {t('assets.create.selects.type', { defaultValue: 'Select type' })}
              </option>
              {assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t('assets.create.fields.owner', { defaultValue: 'Owner' })}
              </label>
              <select
                name="ownerId"
                className="form-select"
                value={formData.ownerId}
                onChange={handleChange}
                required
              >
                <option value="">
                  {t('assets.create.selects.owner', { defaultValue: 'Select owner' })}
                </option>
                {functionsList.map((fn) => (
                  <option key={fn.id} value={fn.id}>
                    {fn.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t('assets.create.fields.custodian', { defaultValue: 'Custodian' })}
              </label>
              <select
                name="custodianId"
                className="form-select"
                value={formData.custodianId}
                onChange={handleChange}
                required
              >
                <option value="">
                  {t('assets.create.selects.custodian', {
                    defaultValue: 'Select custodian',
                  })}
                </option>
                {functionsList.map((fn) => (
                  <option key={fn.id} value={fn.id}>
                    {fn.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t('assets.create.fields.posture', { defaultValue: 'Posture' })}
              </label>
              <select
                name="posture"
                className="form-select"
                value={formData.posture || ''}
                onChange={handleChange}
              >
                <option value="">
                  {t('assets.create.selects.posture', { defaultValue: 'Select posture' })}
                </option>
                <option value="High">
                  {t('assets.create.posture.high', { defaultValue: 'High' })}
                </option>
                <option value="Medium">
                  {t('assets.create.posture.medium', { defaultValue: 'Medium' })}
                </option>
                <option value="Low">
                  {t('assets.create.posture.low', { defaultValue: 'Low' })}
                </option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">
                {t('assets.create.fields.compliance', { defaultValue: 'Compliance' })}
              </label>
              <select
                name="compliance"
                className="form-select"
                value={formData.compliance || ''}
                onChange={handleChange}
              >
                <option value="">
                  {t('assets.create.selects.compliance', {
                    defaultValue: 'Select compliance status',
                  })}
                </option>
                <option value="Fully Implemented">
                  {t('assets.create.compliance.fully', {
                    defaultValue: 'Fully Implemented',
                  })}
                </option>
                <option value="Implemented">
                  {t('assets.create.compliance.impl', { defaultValue: 'Implemented' })}
                </option>
                <option value="Mostly Implemented">
                  {t('assets.create.compliance.mostly', {
                    defaultValue: 'Mostly Implemented',
                  })}
                </option>
                <option value="Partially Implemented">
                  {t('assets.create.compliance.partial', {
                    defaultValue: 'Partially Implemented',
                  })}
                </option>
                <option value="Averagely Implemented">
                  {t('assets.create.compliance.average', {
                    defaultValue: 'Averagely Implemented',
                  })}
                </option>
                <option value="Not Implemented">
                  {t('assets.create.compliance.not', {
                    defaultValue: 'Not Implemented',
                  })}
                </option>
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">
              {t('assets.create.fields.date', { defaultValue: 'Date' })}
            </label>
            <input
              type="date"
              name="date"
              className="form-control"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/assets')}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? t('assets.create.buttons.creating', { defaultValue: 'Creating...' })
                : t('assets.create.buttons.create', { defaultValue: 'Create' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAsset;


